import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { usersService } from '../services/users'

// Session storage key for tracking MFA check status
const MFA_SESSION_CHECK_KEY = 'coformit_mfa_session_check'

const PrivateRoute = ({ children, skipMfaCheck = false }) => {
  const { user, loading, isAuthenticating } = useAuth()
  const location = useLocation()

  // Check if this session has already passed MFA check
  // This prevents interrupting users mid-session when MFA is newly enabled
  const hasPassedMfaCheck = sessionStorage.getItem(MFA_SESSION_CHECK_KEY) === 'passed'

  // Fetch MFA status for the current user (only when user is authenticated)
  // Skip if user already passed MFA check this session
  const { data: mfaStatus, isLoading: mfaLoading } = useQuery({
    queryKey: ['mfaStatus'],
    queryFn: () => usersService.getMfaStatus(),
    enabled: !!user && !loading && !isAuthenticating && !skipMfaCheck && !hasPassedMfaCheck,
    staleTime: 60000, // Cache for 1 minute
    retry: false,
  })

  // Mark session as having passed MFA check when appropriate
  useEffect(() => {
    // If MFA check passed (not required OR user has MFA enabled), mark the session
    if (mfaStatus && !mfaStatus.mfaRequired) {
      sessionStorage.setItem(MFA_SESSION_CHECK_KEY, 'passed')
    }
  }, [mfaStatus])

  // Show loading during initial auth check or while actively authenticating
  if (loading || isAuthenticating) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading...</p>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // If MFA check is not skipped and user hasn't passed this session, check MFA status
  if (!skipMfaCheck && !hasPassedMfaCheck) {
    // Show loading while checking MFA status
    if (mfaLoading) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          <p>Loading...</p>
        </div>
      )
    }

    // If MFA is required and not enabled, redirect to MFA setup
    if (mfaStatus?.mfaRequired === true) {
      return <Navigate to="/mfa-required" replace state={{ from: location }} />
    }
  }

  return children
}

export default PrivateRoute

// Export utility function to clear session MFA check (call on logout)
export const clearMfaSessionCheck = () => {
  sessionStorage.removeItem(MFA_SESSION_CHECK_KEY)
}
